import React, { useState } from 'react';
import { PageHeader, Card, FormGroup, Input, Select, Button, DataTable, ActionButtons, SectionDivider } from '../../components/common/UIComponents';
import { LedgerController, AccountGroupController, AccountSubGroupController } from '../../controllers';
import { AccountHeads } from '../../models';
import { useAsyncData } from '../../hooks/useAsyncData';

const LEDGER_TYPES = ['Payment', 'Supplementary', 'Regular'];

export default function AccountMasterPage() {
  const { data: records, loading, error, reload } = useAsyncData(() => LedgerController.getAll());
  const { data: groups } = useAsyncData(() => AccountGroupController.getAll());
  const { data: subGroups } = useAsyncData(() => AccountSubGroupController.getAll());
  const [ledgerType, setLedgerType] = useState('Payment');
  const [form, setForm] = useState({ societyId: 1, ledgerName: '', accountHead: '', groupId: '', subGroupId: '', openingBalance: '' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);

  const filteredGroups    = groups.filter(g => (g.accountHead||g.head) === form.accountHead);
  const filteredSubGroups = subGroups.filter(s => (s.accountHead||s.head) === form.accountHead);

  const handleSubmit = async () => {
    if (!form.ledgerName) return;
    setSaving(true);
    try {
      const payload = { ...form, openingBalance: parseFloat(form.openingBalance)||0 };
      if (editing) { await LedgerController.update(editing, payload); setEditing(null); }
      else { await LedgerController.add(payload); }
      setForm({ societyId:1, ledgerName:'', accountHead:'', groupId:'', subGroupId:'', openingBalance:'' });
      reload();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: 'id', label: '#' },
    { key: 'ledgerName', label: 'Ledger Name' },
    { key: 'accountHead', label: 'Account Head' },
    { key: 'openingBalance', label: 'Opening Balance', render: r => `₹${(r.openingBalance||0).toLocaleString('en-IN')}` },
    { key: 'actions', label: 'Actions', render: row => (
      <ActionButtons
        onEdit={() => { setForm({ societyId:1, ledgerName:row.ledgerName||row.name||'', accountHead:row.accountHead||row.head||'', groupId:row.groupId||'', subGroupId:row.subGroupId||'', openingBalance:(row.openingBalance||'').toString() }); setEditing(row.id); }}
        onDelete={async () => { try { await LedgerController.delete(row.id); reload(); } catch(e){alert(e.message);} }}
      />
    )},
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Account Master" description="Manage payment and supplementary ledger accounts" />
      <Card title={editing ? 'Edit Ledger' : 'Create Ledger'} style={{ marginBottom: 20 }}>
        <div style={{ marginBottom: 14 }}>
          <div className="radio-group">
            {LEDGER_TYPES.map(t => <label key={t} className="radio-label"><input type="radio" name="ledgerType" checked={ledgerType===t} onChange={()=>setLedgerType(t)} /> {t}</label>)}
          </div>
        </div>
        <div className="form-grid form-grid-3" style={{ marginBottom: 14 }}>
          <FormGroup label="Ledger Name" required>
            <Input placeholder="Enter Ledger Name" value={form.ledgerName} onChange={e=>setForm(p=>({...p,ledgerName:e.target.value}))} />
          </FormGroup>
          <FormGroup label="Account Head">
            <Select value={form.accountHead} onChange={e=>setForm(p=>({...p,accountHead:e.target.value,groupId:'',subGroupId:''}))} options={AccountHeads} placeholder="Select" />
          </FormGroup>
          <FormGroup label="Account Group">
            <Select value={form.groupId} onChange={e=>setForm(p=>({...p,groupId:e.target.value}))} options={filteredGroups.map(g=>({value:g.id,label:g.groupName||g.group}))} placeholder="Select Group" />
          </FormGroup>
          <FormGroup label="Sub Group">
            <Select value={form.subGroupId} onChange={e=>setForm(p=>({...p,subGroupId:e.target.value}))} options={filteredSubGroups.map(s=>({value:s.id,label:s.subGroupName||s.subGroup}))} placeholder="Select Sub Group" />
          </FormGroup>
          <FormGroup label="Opening Balance">
            <Input type="number" placeholder="0.00" value={form.openingBalance} onChange={e=>setForm(p=>({...p,openingBalance:e.target.value}))} />
          </FormGroup>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Button onClick={handleSubmit} disabled={saving}>{saving?'Saving…':editing?'Update Ledger':'Create Ledger'}</Button>
          {editing && <Button variant="ghost" onClick={()=>{setForm({societyId:1,ledgerName:'',accountHead:'',groupId:'',subGroupId:'',openingBalance:''});setEditing(null);}}>Cancel</Button>}
        </div>
      </Card>
      <SectionDivider />
      {loading && <div style={{padding:20,color:'#78909c'}}>Loading…</div>}
      {error   && <div style={{padding:20,color:'#c62828'}}>Error: {error}</div>}
      {!loading && <DataTable title="Ledger Accounts" columns={columns} data={records} />}
    </div>
  );
}
